import React, { useState, useEffect } from "react";
import { Tooltip } from "react-tooltip";
import {
  InformationCircleIcon,
  ArrowLeftCircleIcon,
  ArrowRightCircleIcon,
  ArrowDownTrayIcon,
  ArrowPathIcon,
  ExclamationCircleIcon,
  CheckCircleIcon,
  XCircleIcon,
  QuestionMarkCircleIcon,
  ChevronDownIcon,
  LockClosedIcon,
  PlusIcon,
  MinusIcon,
} from "@heroicons/react/24/outline";
import { requireAuth } from "../auth";

/*
===========================================================================
SeparationCalculator.jsx

Objectif : Estimer les frais potentiels d'une procédure de séparation de corps
au Québec (2025), en guidant l'utilisateur étape par étape.

IMPORTANT : Contrairement au divorce, le demandeur en séparation de corps
paie généralement les frais de dépôt initiaux.
===========================================================================
*/

// Coûts officiels (2025)
const OFFICIAL_FEES = {
  DEMANDE: 355,             // Frais de dépôt de la demande (pour séparation de corps)
  REPONSE: 177,             // Frais pour la réponse/opposition (si vous êtes défendeur)
  DEMANDE_CONJOINTE: 128,   // Frais pour une demande conjointe (amiable)
};

// Fourchettes de coûts
const COST_RANGES = {
  AVOCAT_AMIABLE: { min: 1500, max: 4000 }, // Par partie, avocats séparés (slightly higher for separation)
  AVOCAT_AMIABLE_PARTAGE: { min: 1000, max: 2500 }, // Si partagé (slightly higher)
  AVOCAT_CONTESTE: { min: 6000, max: 15000 }, // Par partie (adjusted range)
  HUISSIER: { min: 80, max: 250 }, // Par service (adjusted range)
  TRADUCTION_PAGE: { min: 50, max: 100 }, // Par page
  FRAIS_ADMIN: { min: 50, max: 150 }, // Frais administratifs divers
};

// Coûts des expertises (Specific to Separation Context)
const EXPERTISE_COSTS = {
  PSYCHOSOCIALE: { min: 1800, max: 4500 }, // For custody/access issues
  IMMOBILIERE_RESIDENTIAL: { min: 400, max: 800 }, // Evaluation of family residence
  IMMOBILIERE_COMMERCIAL: { min: 800, max: 4000 }, // Evaluation of commercial property
  ENTREPRISE: { min: 2000, max: 10000 }, // Business valuation
  PENSION_PARTAGE: { min: 800, max: 3000 }, // Actuarial valuation for pension sharing
};

// Règles spéciales (can be reused)
const SPECIAL_RULES = {
  URGENCE_SURCHARGE: 0.3, // +30% si urgence
  COMPLEXITE_SURCHARGE: 0.5, // +50% si complexe
};

// Palettes de couleurs (reused from other calculators)
const COLORS = {
  primary: {
    light: "#E3F2FD",
    main: "#2196F3",
    dark: "#1565C0",
  },
  secondary: {
    light: "#F3E5F5",
    main: "#9C27B0",
    dark: "#6A1B9A",
  },
  success: {
    light: "#E8F5E9",
    main: "#4CAF50",
    dark: "#2E7D32",
  },
  error: {
    light: "#FFEBEE",
    main: "#F44336",
    dark: "#C62828",
  },
  warning: {
    light: "#FFF8E1",
    main: "#FFC107",
    dark: "#F57F17",
  },
  info: {
    light: "#E1F5FE",
    main: "#03A9F4",
    dark: "#0277BD",
  },
  grey: {
    50: "#FAFAFA",
    100: "#F5F5F5",
    200: "#EEEEEE",
    300: "#E0E0E0",
    400: "#BDBDBD",
    500: "#9E9E9E",
    600: "#757575",
    700: "#616161",
    800: "#424242",
    900: "#212121",
  },
};

const sections = [
  {
    id: "situation",
    title: "Situation Initiale",
    icon: "🧑‍🤝‍🧑",
    description:
      "Décrivez votre situation pour commencer à estimer les coûts de la séparation de corps.",
    questions: [
      {
        name: "role",
        label: "Quel est votre rôle dans la procédure ?",
        type: "radio",
        options: [
          { value: "demandeur", label: "Je dépose la demande (demandeur)" },
          { value: "defendeur", label: "Je réponds / conteste la demande (défendeur)" },
          { value: "conjoint", label: "Nous déposons une demande conjointe" }
        ],
        tooltip: "Votre rôle détermine qui paie certains frais judiciaires initiaux.",
        helpText: "Le demandeur paie généralement les frais de dépôt, sauf si la demande est conjointe."
      },
      {
        name: "contested",
        label: "La séparation est-elle contestée ?",
        type: "radio",
        options: [
          { value: "no", label: "Non (entente amiable / demande conjointe)" },
          { value: "yes", label: "Oui (désaccord sur un ou plusieurs points)" },
        ],
        tooltip: "Une séparation contestée implique généralement des coûts plus élevés et une procédure plus longue.",
        showIf: (ans) => ans.role !== 'conjoint',
      },
      {
        name: "hasChildren",
        type: "radio",
        label: "Avez-vous des enfants mineurs communs ?",
        options: [
            { value: "yes", label: "Oui" },
            { value: "no", label: "Non" },
        ],
        tooltip: "La présence d'enfants peut nécessiter des arrangements spécifiques (garde, pension) et potentiellement une expertise."
      },
    ],
  },
  {
    id: "courtFees",
    title: "Frais de Cour & Procédure",
    icon: "⚖️",
    description:
      "Estimez les frais judiciaires initiaux et les particularités de la procédure.",
    questions: [
      {
        name: "filingType",
        label: "Type de dépôt et frais associés :",
        type: "radio",
        options: [
          {
            value: "conjointe",
            label: `Demande conjointe (${OFFICIAL_FEES.DEMANDE_CONJOINTE}$)`,
            showIf: (ans) => ans.role === 'conjoint' || ans.contested === "no"
          },
          {
            value: "demande",
            label: `Dépôt de la demande (${OFFICIAL_FEES.DEMANDE}$)`,
            showIf: (ans) => ans.role === "demandeur" && ans.contested === "yes"
          },
          {
            value: "reponse",
            label: `Dépôt de réponse/opposition (${OFFICIAL_FEES.REPONSE}$)`,
             showIf: (ans) => ans.role === "defendeur"
          },
           {
             value: "none",
             label: "Je ne paierai pas ces frais (ex: Aide Juridique)",
           }
        ],
        tooltip: "Les frais de cour varient selon le type de demande. En séparation de corps, le demandeur paie généralement les frais de dépôt.",
      },
       {
        name: "needUrgent",
        type: "radio",
        label: "Avez-vous besoin de mesures urgentes/provisoires ?",
        options: [
          { value: "yes", label: "Oui (ex: sauvegarde, provisions pour frais)" },
          { value: "no", label: "Non" },
        ],
        helpText: "Une procédure urgente peut accélérer certaines décisions mais augmenter les honoraires d'avocat."
      },
       {
        name: "district",
        type: "select",
        label: "Dans quel district judiciaire déposerez-vous ?",
        options: [
          { value: "montreal", label: "Montréal (délais possibles)" },
          { value: "quebec", label: "Québec" },
          { value: "laval", label: "Laval" },
          { value: "longueuil", label: "Longueuil" },
          { value: "gatineau", label: "Gatineau" },
          { value: "sherbrooke", label: "Sherbrooke" },
          { value: "troisRivieres", label: "Trois-Rivières" },
          { value: "autre", label: "Autre district" },
        ],
        tooltip:
          "Les délais peuvent varier selon les districts judiciaires.",
      },
    ],
  },
  {
    id: "lawyer",
    title: "Honoraires d'Avocat",
    icon: "👨‍⚖️",
    description:
      "Précisez si vous engagez un avocat et estimez la complexité. Les coûts varient grandement entre une entente (1500-4000$) et une contestation (6000-15000$+).",
    questions: [
      {
        name: "wantLawyer",
        label: "Engager un avocat ?",
        type: "radio",
        options: [
          { value: "true", label: "Oui" },
          { value: "false", label: "Non (0 $)" },
        ],
        tooltip: "Un avocat facilite la procédure mais représente un coût important."
      },
      {
        name: "lawyerShared",
        type: "radio",
        label: "Partagez-vous l'avocat avec l'autre partie ?",
        options: [
          { value: "yes", label: "Oui (pour rédiger l'entente commune)" },
          { value: "no", label: "Non (chacun son avocat ou un seul en a)" },
        ],
        showIf: (ans) => ans.wantLawyer === "true" && (ans.contested === "no" || ans.role === 'conjoint'),
        helpText: "Partager un avocat réduit les coûts mais n'est possible qu'en cas d'entente complète. L'avocat agit comme rédacteur impartial."
      },
      {
        name: "complexityLevel",
        type: "radio",
        label: "Niveau de complexité de votre dossier :",
        options: [
          { value: "simple", label: "Simple (peu de biens, pas d'enfants ou accord total)" },
          { value: "moderate", label: "Modéré (partage de biens standard, quelques points de désaccord)" },
          { value: "complex", label: "Complexe (entreprise, patrimoine important, litige sur la garde, etc.)" },
        ],
        showIf: (ans) => ans.wantLawyer === "true",
        tooltip: "Plus un dossier est complexe (biens, enfants, conflits), plus les honoraires d'avocat seront élevés."
      },
      {
        name: "lawyerFeeType",
        type: "radio",
        label: "Type de tarification de l'avocat",
        options: [
          { value: "range", label: "Fourchette estimée (basée sur la complexité)" },
          { value: "custom", label: "Montant personnalisé (si vous avez un devis)" },
        ],
        showIf: (ans) => ans.wantLawyer === "true",
      },
      {
        name: "customFee",
        type: "number",
        label: "Montant personnalisé ($)",
        showIf: (ans) => ans.wantLawyer === "true" && ans.lawyerFeeType === "custom",
      }
    ],
  },
  {
    id: "services",
    title: "Huissier et Traduction",
    icon: "📨🌐",
    description:
      "Aurez-vous besoin de signifier des documents par huissier ou de faire traduire des documents officiels (ex: certificat de mariage étranger) ?",
    questions: [
      {
        name: "needHuissier",
        label: "Avez-vous besoin d'un huissier ?",
        type: "radio",
        options: [
          { value: "yes", label: "Oui" },
          { value: "no", label: "Non (0 $)" },
        ],
        tooltip: "Un huissier est souvent nécessaire pour remettre officiellement certains documents légaux."
      },
      {
        name: "significationLocation",
        label: "Où se trouve l'autre partie pour la signification ?",
        type: "radio",
        options: [
          { value: "quebec", label: "Au Québec (standard)" },
          { value: "canada", label: "Ailleurs au Canada (coûts plus élevés)" },
          { value: "international", label: "À l'international (coûts et délais accrus)" },
        ],
        showIf: (ans) => ans.needHuissier === "yes",
        tooltip: "L'emplacement de l'autre partie affecte les frais et délais de signification."
      },
      {
        name: "huissierServices",
        type: "slider",
        label: "Nombre de significations requises",
        min: 1,
        max: 10,
        showIf: (ans) => ans.needHuissier === "yes",
        tooltip: "Chaque document à signifier (demande, réponse, etc.) compte comme un service."
      },
      {
        name: "needTranslation",
        label: "Avez-vous des documents à traduire officiellement ?",
        type: "radio",
        options: [
          { value: "yes", label: "Oui" },
          { value: "no", label: "Non (0 $)" },
        ],
        tooltip: "La traduction par un traducteur agréé peut être requise pour les documents officiels dans une autre langue."
      },
      {
        name: "translationPages",
        type: "slider",
        label: "Nombre de pages à traduire",
        min: 1,
        max: 50,
        showIf: (ans) => ans.needTranslation === "yes",
      },
    ],
  },
  {
    id: "expertise",
    title: "Expertises",
    icon: "🔍",
    description:
      "Si des points sont contestés (garde, valeur des biens, pension), une expertise peut être nécessaire pour éclairer le tribunal.",
    questions: [
      {
        name: "needExpertise",
        label: "Une expertise sera-t-elle probablement nécessaire ?",
        type: "radio",
        options: [
          { value: "yes", label: "Oui" },
          { value: "no", label: "Non (0 $)" },
          { value: "unsure", label: "Je ne suis pas sûr(e)" },
        ],
        tooltip: "Les expertises (psychosociale, immobilière, comptable, actuarielle) sont coûteuses mais parfois indispensables en cas de désaccord."
      },
      {
        name: "expertiseType",
        type: "checkbox",
        label: "Quels types d'expertises pourraient être nécessaires ?",
        options: [
          {
            value: "PSYCHOSOCIALE",
            label: `Psychosociale (garde d'enfants) (${EXPERTISE_COSTS.PSYCHOSOCIALE.min}$ - ${EXPERTISE_COSTS.PSYCHOSOCIALE.max}$)`,
            showIf: (ans) => ans.hasChildren === 'yes',
            description: "Évalue la dynamique familiale pour les questions de garde."
          },
          {
            value: "IMMOBILIERE_RESIDENTIAL",
            label: `Immobilière (résidence) (${EXPERTISE_COSTS.IMMOBILIERE_RESIDENTIAL.min}$ - ${EXPERTISE_COSTS.IMMOBILIERE_RESIDENTIAL.max}$)`,
            description: "Évalue la valeur marchande de la résidence familiale."
          },
          {
            value: "ENTREPRISE",
            label: `Évaluation d'entreprise (${EXPERTISE_COSTS.ENTREPRISE.min}$ - ${EXPERTISE_COSTS.ENTREPRISE.max}$)`,
            description: "Détermine la valeur d'une entreprise appartenant à l'un des conjoints."
          },
           {
            value: "PENSION_PARTAGE",
            label: `Actuarielle (partage régime retraite) (${EXPERTISE_COSTS.PENSION_PARTAGE.min}$ - ${EXPERTISE_COSTS.PENSION_PARTAGE.max}$)`,
            description: "Calcule la valeur partageable des droits accumulés dans un régime de retraite."
          },
          {
            value: "IMMOBILIERE_COMMERCIAL",
            label: `Immobilière (commercial) (${EXPERTISE_COSTS.IMMOBILIERE_COMMERCIAL.min}$ - ${EXPERTISE_COSTS.IMMOBILIERE_COMMERCIAL.max}$)`,
            description: "Évalue la valeur d'un bien immobilier commercial."
          }
        ],
        showIf: (ans) => ans.needExpertise === "yes" || ans.needExpertise === "unsure",
        tooltip: "Cochez toutes les expertises potentiellement requises. Les coûts s'additionnent."
      },
       {
        name: "adminFees",
        type: "radio",
        label: "Prévoyez-vous des frais administratifs divers (photocopies, etc.) ?",
        options: [
          { value: "minimal", label: `Minimaux (${COST_RANGES.FRAIS_ADMIN.min} $)` },
          { value: "standard", label: `Standards (~100 $)` },
          { value: "extensive", label: `Importants (${COST_RANGES.FRAIS_ADMIN.max} $)` },
          { value: "none", label: "Aucun (0 $)" },
        ],
        tooltip: "Inclut les photocopies, frais postaux, certifications, etc.",
      },
    ],
  },
  {
    id: "summary",
    title: "Résumé et Économies",
    icon: "💰",
    description:
      "Consultez votre estimation de coûts pour une séparation de corps et découvrez comment économiser.",
    questions: [],
  },
];

function useLocalStorage(key, initialValue) {
  const [storedValue, setStoredValue] = useState(() => {
    try {
      const item = window.localStorage.getItem(key);
      return item ? JSON.parse(item) : initialValue;
    } catch (error) {
      console.log("Error reading from localStorage:", error);
      return initialValue;
    }
  });

  const setValue = (value) => {
    try {
      const valueToStore =
        value instanceof Function ? value(storedValue) : value;
      setStoredValue(valueToStore);
      window.localStorage.setItem(key, JSON.stringify(valueToStore));
    } catch (error) {
      console.log("Error writing to localStorage:", error);
    }
  };

  return [storedValue, setValue];
}

function CostBreakdownDisplay({ lineItems }) {
  const totalMin = lineItems.reduce((sum, item) => sum + item.min, 0);
  const itemsWithPercentage = lineItems.map(item => ({
    ...item,
    percentage: totalMin > 0 ? Math.round((item.min / totalMin) * 100) : 0,
  }));

  return (
    <div className="w-full space-y-4">
      {itemsWithPercentage
        .filter(item => item.min > 0 || item.max > 0)
        .sort((a, b) => b.min - a.min)
        .map((item, index) => (
        <div key={index} className="w-full">
          <div className="flex justify-between mb-1">
            <span className="text-sm font-medium text-slate-700">{item.label}</span>
            <span className="text-sm font-medium text-slate-500">{item.percentage}%</span>
          </div>
          <progress
            className="progress progress-primary w-full h-2"
            value={item.percentage}
            max="100"
            style={{backgroundColor: COLORS.primary.light, color: COLORS.primary.main}}
          ></progress>
          <div className="text-xs text-slate-500 text-right mt-1">
            {item.min.toLocaleString()}$ - {item.max.toLocaleString()}$
          </div>
        </div>
      ))}
    </div>
  );
}

function SeparationCalculator() {
  const [activeSectionId, setActiveSectionId] = useState(sections[0].id);
  const [completedSections, setCompletedSections] = useLocalStorage("separationCostCompletedSections", {});
  const [answers, setAnswers] = useLocalStorage("separationCostAnswers", {});
  const [lineItems, setLineItems] = useState([]);
  const [totalCost, setTotalCost] = useState({ min: 0, max: 0 });
  const [showResetConfirm, setShowResetConfirm] = useState(false);
  const [showLegalAidInfo, setShowLegalAidInfo] = useState(true);
  const [faqOpenIndex, setFaqOpenIndex] = useState(null);

  const progressPercent = Math.round(
    (Object.keys(completedSections).filter(id => completedSections[id] && sections.find(s => s.id === id && s.questions.length > 0)).length / (sections.length - 1)) * 100
  );

  useEffect(() => {
    calculateCosts();
    const currentSection = sections.find(s => s.id === activeSectionId);
    if (currentSection && isSectionComplete(currentSection.id)) {
      if (!completedSections[activeSectionId]) {
        setCompletedSections(prev => ({ ...prev, [activeSectionId]: true }));
      }
    }
  }, [answers, activeSectionId]);

  const handleChange = (name, value) => {
    setAnswers((prev) => {
      const newAnswers = { ...prev, [name]: value };
      try {
        localStorage.setItem("separationCostAnswers", JSON.stringify(newAnswers));
      } catch (e) {
        console.log("Error saving to localStorage:", e);
      }
      return newAnswers;
    });
    calculateCosts();
    const currentSection = sections.find(s => s.id === activeSectionId);
    if (currentSection && isSectionComplete(currentSection.id)) {
       if (!completedSections[activeSectionId]) {
          setCompletedSections(prev => ({ ...prev, [activeSectionId]: true }));
       }
    } else {
      if (completedSections[activeSectionId]) {
        setCompletedSections(prev => ({ ...prev, [activeSectionId]: false }));
      }
    }
  };

  const handleNextSection = () => {
    const currentIndex = sections.findIndex(s => s.id === activeSectionId);
     if (isSectionComplete(activeSectionId)) {
        if (!completedSections[activeSectionId]) {
          setCompletedSections(prev => ({ ...prev, [activeSectionId]: true }));
        }
        if (currentIndex < sections.length - 1) {
          setActiveSectionId(sections[currentIndex + 1].id);
          window.scrollTo(0, 0);
        }
      } else {
        console.log("Please complete the current section.");
      }
  };

  const handlePrevSection = () => {
    const currentIndex = sections.findIndex(s => s.id === activeSectionId);
    if (currentIndex > 0) {
      setActiveSectionId(sections[currentIndex - 1].id);
      window.scrollTo(0, 0);
    }
  };

  const goToSection = (sectionId) => {
     const targetIndex = sections.findIndex(s => s.id === sectionId);
     const currentIndex = sections.findIndex(s => s.id === activeSectionId);

     let canNavigate = false;
     if (targetIndex <= currentIndex) {
       canNavigate = true;
     } else {
       let previousSectionsComplete = true;
       for (let i = 0; i < targetIndex; i++) {
          if (!completedSections[sections[i].id] && sections[i].questions.length > 0) {
              previousSectionsComplete = false;
              break;
            }
       }
       if (previousSectionsComplete) {
         canNavigate = true;
       }
     }

     if (canNavigate) {
       setActiveSectionId(sectionId);
       window.scrollTo(0, 0);
     } else {
       console.log("Please complete previous sections first.");
     }
  };

  const handleReset = () => {
    if (showResetConfirm) {
      setAnswers({});
      setCompletedSections({});
      setShowResetConfirm(false);
      setActiveSectionId(sections[0].id);
      window.scrollTo(0, 0);
    } else {
      setShowResetConfirm(true);
      setTimeout(() => setShowResetConfirm(false), 5000);
    }
  };

  const isSectionComplete = (sectionId) => {
    const section = sections.find(s => s.id === sectionId);
    if (!section || section.questions.length === 0) return true;

    return section.questions.every((q) => {
      if (q.showIf && !q.showIf(answers)) return true;
      const answer = answers[q.name];
      if (q.type === 'checkbox') {
         return answer !== undefined;
      }
      if (q.type === 'slider') {
          return answer !== undefined && answer !== null && answer !== "";
      }
      return answer !== undefined && answer !== null && answer !== "";
    });
  };

  const calculateCosts = () => {
    let minSum = 0;
    let maxSum = 0;
    const newLineItems = [];

    if (answers.filingType && answers.filingType !== "none") {
      let fee = 0;
      let label = "Frais de cour";
      switch (answers.filingType) {
        case "conjointe":
          fee = OFFICIAL_FEES.DEMANDE_CONJOINTE;
          label = "Frais de cour (demande conjointe)";
          break;
        case "demande":
          fee = OFFICIAL_FEES.DEMANDE;
          label = "Frais de cour (dépôt demande)";
          break;
        case "reponse":
          fee = OFFICIAL_FEES.REPONSE;
          label = "Frais de cour (dépôt réponse)";
          break;
        default:
          fee = 0;
      }

      if (fee > 0) {
        newLineItems.push({
          label: label,
          min: fee,
          max: fee,
          icon: "⚖️",
          percentage: 0,
        });
        minSum += fee;
        maxSum += fee;
      }
    }

    if (answers.wantLawyer === "true") {
      let lawyerCostMin, lawyerCostMax;
      const isContested = answers.contested === "yes";

      if (answers.lawyerFeeType === "custom" && answers.customFee && !isNaN(Number(answers.customFee))) {
        const custom = Number(answers.customFee);
        lawyerCostMin = custom;
        lawyerCostMax = custom;
      } else {
        let range;
        if (!isContested) {
          range = answers.lawyerShared === "yes"
            ? COST_RANGES.AVOCAT_AMIABLE_PARTAGE
            : COST_RANGES.AVOCAT_AMIABLE;
        } else {
          range = COST_RANGES.AVOCAT_CONTESTE;
        }

        lawyerCostMin = range.min;
        lawyerCostMax = range.max;

        if (answers.complexityLevel === "complex") {
          lawyerCostMin *= (1 + SPECIAL_RULES.COMPLEXITE_SURCHARGE);
          lawyerCostMax *= (1 + SPECIAL_RULES.COMPLEXITE_SURCHARGE);
        } else if (answers.complexityLevel === "moderate") {
          lawyerCostMin *= (1 + SPECIAL_RULES.COMPLEXITE_SURCHARGE / 2);
          lawyerCostMax *= (1 + SPECIAL_RULES.COMPLEXITE_SURCHARGE / 2);
        }
      }

      if (answers.needUrgent === "yes") {
        lawyerCostMin *= (1 + SPECIAL_RULES.URGENCE_SURCHARGE);
        lawyerCostMax *= (1 + SPECIAL_RULES.URGENCE_SURCHARGE);
      }

      if (lawyerCostMin > 0 || lawyerCostMax > 0) {
         newLineItems.push({
           label: "Honoraires d'avocat",
           min: Math.round(lawyerCostMin),
           max: Math.round(lawyerCostMax),
           icon: "👨‍⚖️",
           percentage: 0,
         });
         minSum += Math.round(lawyerCostMin);
         maxSum += Math.round(lawyerCostMax);
      }
    }

    if (answers.needHuissier === "yes" && answers.huissierServices && Number(answers.huissierServices) > 0) {
      const services = Number(answers.huissierServices);
      const huissierCost = COST_RANGES.HUISSIER;
      let minHuissier = huissierCost.min * services;
      let maxHuissier = huissierCost.max * services;
      let locationMultiplier = 1;
      let locationLabel = "au Québec";

      switch (answers.significationLocation) {
        case "canada":
          locationMultiplier = 2.5;
          locationLabel = "ailleurs au Canada";
          break;
        case "international":
          locationMultiplier = 4;
          locationLabel = "à l'international";
          break;
        default:
          locationMultiplier = 1;
          break;
      }

      minHuissier *= locationMultiplier;
      maxHuissier *= locationMultiplier;

      newLineItems.push({
        label: `Huissier (${services} service${services > 1 ? 's' : ''} ${locationLabel})`,
        min: Math.round(minHuissier),
        max: Math.round(maxHuissier),
        icon: "📨",
        percentage: 0,
      });
      minSum += Math.round(minHuissier);
      maxSum += Math.round(maxHuissier);
    }

    if (answers.needTranslation === "yes" && answers.translationPages && Number(answers.translationPages) > 0) {
      const pages = Number(answers.translationPages);
      const tradCost = COST_RANGES.TRADUCTION_PAGE;
      const minTrad = tradCost.min * pages;
      const maxTrad = tradCost.max * pages;
      newLineItems.push({
        label: `Traduction (${pages} page${pages > 1 ? 's' : ''})`,
        min: Math.round(minTrad),
        max: Math.round(maxTrad),
        icon: "🌐",
        percentage: 0,
      });
      minSum += Math.round(minTrad);
      maxSum += Math.round(maxTrad);
    }

    if (answers.needExpertise === "yes" || answers.needExpertise === "unsure") {
      answers.expertiseType.forEach((type) => {
        let expertiseCost;
        let label;
        let icon = "🔍";

        switch (type) {
          case "PSYCHOSOCIALE":
            expertiseCost = EXPERTISE_COSTS.PSYCHOSOCIALE;
            label = "Expertise psychosociale";
            icon = "👨‍👩‍👧‍👦";
            break;
          case "IMMOBILIERE_RESIDENTIAL":
            expertiseCost = EXPERTISE_COSTS.IMMOBILIERE_RESIDENTIAL;
            label = "Évaluation immobilière (résidence)";
            icon = "🏠";
            break;
          case "ENTREPRISE":
            expertiseCost = EXPERTISE_COSTS.ENTREPRISE;
            label = "Évaluation d'entreprise";
            icon = "💼";
            break;
          case "PENSION_PARTAGE":
            expertiseCost = EXPERTISE_COSTS.PENSION_PARTAGE;
            label = "Évaluation partage retraite (actuaire)";
            icon = "💹";
            break;
          case "IMMOBILIERE_COMMERCIAL":
            expertiseCost = EXPERTISE_COSTS.IMMOBILIERE_COMMERCIAL;
            label = "Évaluation immobilière (commercial)";
            icon = "🏢";
            break;
          default:
            return;
        }

        newLineItems.push({
          label,
          min: expertiseCost.min,
          max: expertiseCost.max,
          icon,
          percentage: 0,
        });
        minSum += expertiseCost.min;
        maxSum += expertiseCost.max;
      });
    }

    if (answers.adminFees && answers.adminFees !== "none") {
      let adminMin = 0, adminMax = 0;

      switch (answers.adminFees) {
        case "minimal":
          adminMin = COST_RANGES.FRAIS_ADMIN.min;
          adminMax = COST_RANGES.FRAIS_ADMIN.min;
          break;
        case "standard":
          adminMin = COST_RANGES.FRAIS_ADMIN.min + 50;
          adminMax = COST_RANGES.FRAIS_ADMIN.min + 50;
          break;
        case "extensive":
          adminMin = COST_RANGES.FRAIS_ADMIN.max;
          adminMax = COST_RANGES.FRAIS_ADMIN.max;
          break;
        default:
          adminMin = adminMax = 0;
      }

      if (adminMin > 0 || adminMax > 0) {
          newLineItems.push({
            label: "Frais administratifs",
            min: adminMin,
            max: adminMax,
            icon: "📋",
            percentage: 0,
          });
          minSum += adminMin;
          maxSum += adminMax;
      }
    }

    setLineItems(newLineItems);
    setTotalCost({ min: Math.round(minSum), max: Math.round(maxSum) });
  };

  const shareSummary = () => {
    const summaryText = `Estimation des coûts pour ma séparation de corps: ${totalCost.min.toLocaleString()}$ - ${totalCost.max.toLocaleString()}$. Détails: ${JSON.stringify(answers)}`;
    navigator.clipboard.writeText(summaryText)
      .then(() => {
        alert("Résumé de l'estimation copié dans le presse-papiers !");
      })
      .catch(err => {
        console.error('Failed to copy: ', err);
        alert("Erreur lors de la copie du résumé.");
      });
  };

  const renderSlider = (question) => {
    const value = parseInt(answers[question.name]) || question.min;
    return (
      <div className="w-full">
        <div className="flex items-center gap-4">
          <input
            type="range"
            min={question.min}
            max={question.max}
            value={value}
            onChange={(e) => handleChange(question.name, e.target.value)}
            className="range range-primary w-full"
          />
          <span className="bg-blue-100 text-blue-700 font-medium px-3 py-1 rounded-full w-16 text-center">
            {value}
          </span>
        </div>
         <div className="flex justify-between text-xs text-slate-500 mt-1 px-1">
          <span>{question.min}</span>
          <span>{question.max}</span>
        </div>
      </div>
    );
  };

  const renderQuestion = (question) => {
    if (question.showIf && !question.showIf(answers)) {
      return null;
    }
    const questionKey = `${activeSectionId}-${question.name}`;

    return (
       <div key={questionKey} className="p-4 rounded-lg border border-slate-200 bg-white shadow-sm">
         <div className="flex items-start justify-between gap-2">
           <label className="font-medium text-slate-800 flex-1">
             {question.label}
           </label>
           {question.tooltip ? (
             <>
               <Tooltip id={`tooltip-${questionKey}`} place="top" className="z-50 max-w-xs" />
               <button
                 type="button"
                 className="text-blue-500 hover:text-blue-700 flex-shrink-0"
                 data-tooltip-id={`tooltip-${questionKey}`}
                 data-tooltip-content={question.tooltip}
                 aria-label="Plus d'informations"
               >
                 <InformationCircleIcon className="w-5 h-5" />
               </button>
             </>
           ) : null}
         </div>

         <div className="mt-3 space-y-2">
            {question.type === "radio" && (
              <div className="space-y-2">
                {question.options.map((opt, i) => {
                  if (opt.showIf && !opt.showIf(answers)) return null;
                  const isChecked = answers[question.name] === opt.value;
                  const optionKey = `${questionKey}-opt-${i}`;
                  return (
                    <label
                      key={optionKey}
                      className={`cursor-pointer flex items-start gap-3 p-3 rounded-md border transition-colors ${
                        isChecked ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:bg-slate-50'
                      }`}
                    >
                      <input
                        type="radio"
                        name={question.name}
                        value={opt.value}
                        checked={isChecked}
                        onChange={(e) => handleChange(question.name, e.target.value)}
                        className="radio radio-primary mt-1 flex-shrink-0"
                      />
                      <span className="flex-1">
                        <span className="text-slate-700 block">{opt.label}</span>
                      </span>
                    </label>
                  );
                })}
              </div>
            )}
            {question.type === "checkbox" && (
               <div className="space-y-3">
                 {question.options.map((opt, i) => {
                    if (opt.showIf && !opt.showIf(answers)) return null;
                    const isChecked = answers[question.name]?.includes(opt.value) || false;
                    const optionKey = `${questionKey}-opt-${i}`;
                    return (
                       <div key={optionKey} className={`rounded-lg border p-4 transition-colors ${isChecked ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:bg-slate-50'}`}>
                         <label className="flex items-start gap-3 cursor-pointer">
                           <input
                             type="checkbox"
                             value={opt.value}
                             checked={isChecked}
                             onChange={(e) => {
                               const currentValues = answers[question.name] || [];
                               const newValues = e.target.checked
                                 ? [...currentValues, opt.value]
                                 : currentValues.filter(v => v !== opt.value);
                               handleChange(question.name, newValues);
                             }}
                             className="mt-1 checkbox checkbox-primary flex-shrink-0"
                           />
                           <div className="flex-1">
                             <div className="font-medium text-slate-700">{opt.label}</div>
                             {opt.description && (
                               <p className="text-sm text-slate-500 mt-1">{opt.description}</p>
                             )}
                           </div>
                         </label>
                       </div>
                    );
                 })}
               </div>
            )}
            {question.type === "select" && (
              <select
                className="select select-bordered w-full"
                value={answers[question.name] || ""}
                onChange={(e) => handleChange(question.name, e.target.value)}
              >
                <option value="" disabled>Sélectionnez...</option>
                {question.options.map((o, i) => (
                  <option key={i} value={o.value}>{o.label}</option>
                ))}
              </select>
            )}
            {question.type === "slider" && renderSlider(question)}
            {question.type === "number" && (
               <div className="relative mt-1 rounded-md shadow-sm w-full max-w-xs">
                   <input
                       type="number"
                       value={answers[question.name] || ""}
                       onChange={(e) => handleChange(question.name, e.target.value)}
                       className="input input-bordered w-full pr-12"
                       placeholder="0"
                       min="0"
                   />
                   <div className="absolute inset-y-0 right-0 pr-3 flex items-center pointer-events-none">
                       <span className="text-gray-500 sm:text-sm">$</span>
                   </div>
               </div>
            )}
         </div>
         {question.helpText && !question.tooltip && (
           <p className="text-xs text-slate-500 mt-2">{question.helpText}</p>
         )}
       </div>
    );
  };

  const SEPARATION_SAVING_TIPS = [
     {
      title: "Privilégiez la médiation familiale",
      description: "Pour les questions liées aux enfants ou aux conséquences de la séparation, la médiation (5h gratuites si enfants) est souvent la voie la moins coûteuse.",
      potential_savings: "Évite frais d'avocat élevés et frais de cour (si entente)",
      difficulty: "Facile à Modérée",
      relevance: () => true
    },
    {
      title: "Optez pour une demande conjointe",
      description: "Si vous êtes d'accord sur tous les points (garde, pension, partage des biens), une demande conjointe réduit les frais de cour et les honoraires d'avocat.",
      potential_savings: `${OFFICIAL_FEES.DEMANDE - OFFICIAL_FEES.DEMANDE_CONJOINTE}$ frais cour + honoraires avocat réduits`,
      difficulty: "Facile (si accord)",
      relevance: (ans) => ans.contested === 'no' || ans.role === 'conjoint'
    },
    {
      title: "Préparez votre dossier minutieusement",
      description: "Rassemblez tous les documents financiers (relevés, actifs, dettes, REER, etc.) et informations pertinentes pour optimiser le temps (et les frais) passé avec votre avocat.",
      potential_savings: "500$ - 2000$+ en honoraires",
      difficulty: "Facile",
      relevance: (ans) => ans.wantLawyer === 'true'
    },
    {
      title: "Vérifiez l'aide juridique",
      description: "Si vos revenus sont modestes, vous pourriez être admissible à l'aide juridique qui couvrirait une partie ou la totalité des frais légaux.",
      potential_savings: "Totalité ou partie des frais",
      difficulty: "Facile",
      relevance: () => true
    }
  ];

  const faqItems = [
    {
      question: "Quelle est la différence entre séparation de corps et divorce ?",
      answer:
        "La séparation de corps met fin à l'obligation de faire vie commune mais maintient le lien matrimonial. Le divorce dissout complètement le mariage. Les conséquences sur le partage des biens (patrimoine familial, régime matrimonial) sont similaires, mais le droit à une pension alimentaire entre ex-époux après séparation est plus limité qu'après divorce.",
    },
    {
      question: "Combien de temps dure une procédure de séparation de corps ?",
      answer:
        "Une séparation à l'amiable (demande conjointe) peut prendre 3 à 6 mois. Une procédure contestée peut s'étendre sur 6 à 18 mois, voire plus, selon la complexité et l'engorgement des tribunaux.",
    },
     {
      question: "Quelles sont les conséquences de la séparation de corps ?",
      answer:
        "Elle met fin à la vie commune, entraîne le partage du patrimoine familial et la dissolution du régime matrimonial. Elle fixe aussi les modalités de garde des enfants et de pension alimentaire (enfants et parfois ex-conjoint). Le lien du mariage subsiste.",
    },
     {
      question: "Peut-on se séparer sans passer par le tribunal ?",
      answer:
        "Vous pouvez vous entendre à l'amiable sur tous les aspects (séparation de fait), mais pour que la séparation soit légalement reconnue (séparation de corps), un jugement du tribunal est nécessaire. Une entente rédigée par un avocat ou notaire est fortement recommandée même en cas de séparation de fait.",
    },
  ];

  const renderSection = (section) => {
    const isCurrent = section.id === activeSectionId;
    const isCompleted = completedSections[section.id] || false;
    const sectionIndex = sections.findIndex(s => s.id === section.id);
    const isSummary = section.id === 'summary';

    let isAccessible = false;
    if (sectionIndex === 0) {
        isAccessible = true;
      } else {
        let previousSectionsComplete = true;
        for (let i = 0; i < sectionIndex; i++) {
           if (!completedSections[sections[i].id] && sections[i].questions.length > 0) {
             previousSectionsComplete = false;
             break;
           }
        }
        isAccessible = previousSectionsComplete;
     }

    if (isSummary && isAccessible && !completedSections[section.id]) {
       setCompletedSections(prev => ({ ...prev, [section.id]: true }));
    }

    return (
      <div key={section.id} className={`mb-6 rounded-xl border shadow-sm transition-all duration-300 ${
        isCurrent ? 'border-blue-300 bg-blue-50 shadow-md' : isCompleted ? 'border-green-300 bg-green-50' : 'border-slate-200 bg-white'
      } ${isAccessible ? 'opacity-100' : 'opacity-60 cursor-not-allowed'}`}>
        <button
          className={`w-full p-5 text-left flex justify-between items-center ${isAccessible ? 'cursor-pointer hover:bg-opacity-50' : 'cursor-not-allowed'} ${isCurrent ? '' : isCompleted ? 'hover:bg-green-100' : 'hover:bg-slate-50'}`}
          onClick={() => isAccessible && goToSection(section.id)}
          disabled={!isAccessible}
          aria-expanded={isCurrent}
          aria-controls={`section-content-${section.id}`}
        >
          <div className="flex items-center">
            <span className={`mr-3 text-xl ${isCompleted && !isCurrent ? 'text-green-600' : isCurrent ? 'text-blue-600' : 'text-slate-500'}`}>
              {section.icon}
            </span>
            <h2 className={`text-lg font-semibold ${isCompleted && !isCurrent ? 'text-green-800' : isCurrent ? 'text-blue-800' : 'text-slate-700'}`}>
              {section.title}
            </h2>
          </div>
          <div className="flex items-center">
            {isCompleted && !isCurrent && (
              <CheckCircleIcon className="w-6 h-6 text-green-500 mr-2" aria-label="Complété"/>
            )}
             <span className={`transform transition-transform duration-200 ${isCurrent ? 'rotate-180' : 'rotate-0'}`}>
               {isAccessible ? <ChevronDownIcon className="w-5 h-5 text-slate-500" /> : <LockClosedIcon className="w-5 h-5 text-slate-400" aria-label="Verrouillé"/> }
             </span>
          </div>
        </button>

        {isCurrent && (
          <div id={`section-content-${section.id}`} className="px-5 pb-6 pt-2 border-t border-slate-200">
             {isSummary ? (
               renderCostSummary()
             ) : (
               <>
                 <p className="text-sm text-slate-600 mb-6">{section.description}</p>
                 <div className="space-y-6">
                  {section.questions.map((question) => renderQuestion(question))}
                  </div>
                  <div className="mt-8 flex justify-between items-center pt-4 border-t border-slate-200">
                    <div>
                      {sectionIndex > 0 && (
                        <button onClick={handlePrevSection} className="btn btn-outline btn-primary btn-sm gap-1">
                          <ArrowLeftCircleIcon className="w-4 h-4" /> Précédent
                        </button>
                      )}
                    </div>
                    <div>
                      {sectionIndex < sections.length - 1 && (
                        <button
                          onClick={handleNextSection}
                          className={`btn btn-primary btn-sm gap-1 ${!isSectionComplete(section.id) ? 'opacity-50 cursor-not-allowed' : ''}`}
                        >
                          { isSectionComplete(section.id) ? 'Suivant' : 'Complétez la section'}
                           {!isSectionComplete(section.id) && <LockClosedIcon className="w-4 h-4 ml-1" />}
                           {isSectionComplete(section.id) && <ArrowRightCircleIcon className="w-4 h-4" />}
                        </button>
                      )}
                    </div>
                  </div>
                </>
              )}
          </div>
        )}
      </div>
    );
  };

  const renderCostSummary = () => {
    const relevantTips = SEPARATION_SAVING_TIPS.filter(tip => tip.relevance(answers));

    return (
      <div className="space-y-8">
        <div className="grid md:grid-cols-2 gap-6">
          <div className="card bg-gradient-to-br from-blue-500 to-blue-700 text-white p-6 rounded-xl shadow-lg">
            <h3 className="font-semibold mb-3 text-lg flex items-center opacity-90">
              <span className="mr-2">💰</span> Estimation Totale (Séparation)
            </h3>
            <p className="text-4xl font-bold">
              {totalCost.min.toLocaleString()}$ - {totalCost.max.toLocaleString()}$
            </p>
            <p className="opacity-80 mt-1 text-sm">Coût total estimé de la procédure</p>
            <p className="text-xs opacity-70 mt-3 italic">Estimation indicative. Les coûts réels varient.</p>
          </div>
          <div className="card bg-white border border-slate-200 p-6 rounded-xl shadow">
            <h3 className="font-semibold text-slate-700 mb-3 flex items-center">
              <span className="mr-2">📊</span> Facteurs Clés Influents
            </h3>
            <ul className="space-y-2 text-sm">
               <li className="flex justify-between items-center">
                 <span className="text-slate-600">Rôle:</span>
                 <span className="font-medium capitalize px-2 py-0.5 rounded text-xs bg-indigo-100 text-indigo-800">
                   {answers.role || 'N/A'}
                 </span>
               </li>
               <li className="flex justify-between items-center">
                 <span className="text-slate-600">Type:</span>
                 <span className={`font-medium px-2 py-0.5 rounded text-xs ${answers.contested === 'yes' ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                   {answers.contested === 'yes' ? 'Contesté' : 'À l\'amiable'}
                 </span>
               </li>
                <li className="flex justify-between items-center">
                  <span className="text-slate-600">Avocat:</span>
                   <span className={`font-medium px-2 py-0.5 rounded text-xs ${answers.wantLawyer === 'true' ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-800'}`}>
                     {answers.wantLawyer === 'true' ? 'Oui' : 'Non'}
                   </span>
                </li>
            </ul>
            <p className="text-xs text-slate-400 mt-3 italic">Principaux éléments affectant le coût.</p>
          </div>
        </div>

        <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6">
          <h2 className="text-xl font-bold text-blue-700 mb-4 flex items-center gap-2">
            <span className="bg-blue-50 p-2 rounded-full">📊</span>
            Détail des coûts estimés
          </h2>
          {lineItems.length > 0 && (totalCost.min > 0 || totalCost.max > 0) ? (
            <CostBreakdownDisplay lineItems={lineItems} />
          ) : (
            <div className="flex flex-col items-center justify-center h-32 bg-slate-50 rounded-lg">
              <ExclamationCircleIcon className="w-12 h-12 text-slate-300 mb-2" />
              <p className="text-center text-slate-500 italic">
                Complétez les sections pour voir le détail.
              </p>
            </div>
          )}
        </div>

        <div className="bg-green-50 rounded-xl border border-green-200 shadow-sm p-6">
          <h2 className="text-xl font-bold text-green-700 mb-4 flex items-center gap-2">
            <span className="bg-green-100 p-2 rounded-full">💡</span>
            Astuces personnalisées pour économiser (Séparation)
          </h2>
          <div className="space-y-4">
            {relevantTips.length > 0 ? relevantTips.slice(0, 3).map((tip, index) => (
              <div key={index} className="flex items-start gap-4 bg-white p-4 rounded-lg border border-green-100 shadow-sm">
                <div className="bg-green-100 p-2 rounded-full flex-shrink-0 mt-1">
                  <CheckCircleIcon className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <h4 className="font-semibold text-green-800">{tip.title}</h4>
                  <p className="text-green-700 text-sm mt-1">{tip.description}</p>
                  {tip.potential_savings && <p className="text-xs text-green-600 font-medium mt-1">Économie potentielle: <span className="font-bold">{tip.potential_savings}</span></p>}
                </div>
              </div>
            )) : <p className="text-green-700 italic text-sm">Aucune astuce spécifique pour le moment.</p>}
          </div>
        </div>

        <div className="bg-blue-50 rounded-xl border border-blue-200 shadow-sm p-6">
          <h2 className="text-xl font-bold text-blue-700 mb-4 flex items-center gap-2">
            <span className="bg-blue-100 p-2 rounded-full">❓</span>
            Questions fréquentes (Séparation)
          </h2>
          <div className="space-y-4">
            {faqItems.map((item, index) => (
              <div key={index} className="border-b border-slate-100 last:border-0 pb-4 last:pb-0">
                <button
                  className="w-full text-left font-medium text-slate-700 flex items-center justify-between p-2 hover:bg-slate-50 rounded transition-colors"
                  onClick={() => setFaqOpenIndex(faqOpenIndex === index ? null : index)}
                  aria-expanded={faqOpenIndex === index}
                  aria-controls={`faq-answer-sep-${index}`}
                >
                  <span>{item.question}</span>
                  <span className="text-blue-500 text-xl font-light">
                    {faqOpenIndex === index ? <MinusIcon className="w-4 h-4" /> : <PlusIcon className="w-4 h-4" />}
                  </span>
                </button>
                {faqOpenIndex === index && (
                  <p id={`faq-answer-sep-${index}`} className="mt-2 text-slate-600 text-sm p-2 bg-slate-50 rounded">
                    {item.answer}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>

        <div className="pt-6 flex justify-center">
          <button
            onClick={handleReset}
             className={`btn ${
              showResetConfirm
                ? 'btn-error text-white'
                : 'btn-outline border-red-300 text-red-700 hover:bg-red-50 hover:border-red-400'
            } btn-sm transition-colors gap-2`}
          >
            <ArrowPathIcon className="w-4 h-4" />
            {showResetConfirm ? 'Confirmer la réinitialisation ?' : 'Réinitialiser le calculateur'}
          </button>
        </div>

      </div>
    );
  };

  const renderLegalAidInfo = () => {
    if (!showLegalAidInfo) return null;

    return (
      <div
        className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 transition-opacity duration-300"
        style={{ backgroundColor: COLORS.info.light, borderLeftColor: COLORS.info.main }}
      >
        <div className="flex justify-between items-start">
          <h3 className="text-blue-800 font-semibold mb-2" style={{ color: COLORS.info.dark }}>
            Aide Juridique
          </h3>
          <button
            onClick={() => setShowLegalAidInfo(false)}
            className="text-gray-500 hover:text-gray-700 -mt-1 -mr-1"
            aria-label="Fermer"
          >
            <XCircleIcon className="w-5 h-5" />
          </button>
        </div>
        <p className="text-sm text-gray-700 mb-2">
          <strong>Si vos revenus sont modestes, vérifiez votre admissibilité à l'aide juridique.</strong> 
          Vous pourriez bénéficier d'un avocat gratuit ou à faible coût pour votre procédure de séparation de corps.
          <a
            href="https://www.csj.qc.ca/commission-des-services-juridiques/aide-juridique/admissibilite/fr"
            className="text-blue-600 hover:underline ml-1 font-medium"
            target="_blank" rel="noopener noreferrer" style={{ color: COLORS.primary.main }}
          >
            Vérifier l'admissibilité (CSJ)
          </a>
        </p>
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-5xl mx-auto pb-12 px-4">

        <div className="px-4 md:px-6 py-8 mt-6 bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl border border-indigo-100 shadow-sm mb-8">
           <h1 className="text-2xl md:text-3xl font-bold text-indigo-700 mb-3 text-center">
             <span className="inline-block h-1 bg-purple-400 w-8 mr-2 rounded-full align-middle"></span>
             Calculateur des Coûts (Séparation de Corps)
             <span className="inline-block h-1 bg-purple-400 w-8 ml-2 rounded-full align-middle"></span>
           </h1>
           <p className="text-center text-indigo-600 max-w-3xl mx-auto">
             Estimez les coûts potentiels de votre procédure de séparation de corps selon votre situation personnelle.
           </p>
         </div>

        <div className="grid md:grid-cols-2 gap-6 mb-8">
           <div className="card bg-white border border-slate-200 shadow-sm p-6 transition-all duration-200 hover:shadow-md">
             <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
               <span className="bg-blue-50 p-2 rounded-full mr-2">💰</span>
               Estimation des coûts
             </h3>
             <div className="flex items-center justify-center h-16">
               {totalCost.min === 0 && totalCost.max === 0 && Object.keys(answers).length > 0 ? (
                  <p className="text-green-600 text-center text-lg font-bold">0$ (selon vos choix)</p>
               ) : totalCost.min === 0 && totalCost.max === 0 ? (
                 <p className="text-slate-500 text-center text-sm">Complétez le questionnaire</p>
               ) : (
                 <div className="text-center">
                   <p className="text-2xl font-bold text-blue-700">
                     {totalCost.min.toLocaleString()}$ - {totalCost.max.toLocaleString()}$
                   </p>
                   <p className="text-sm text-slate-600 mt-1">coût total estimé</p>
                 </div>
               )}
             </div>
           </div>
           <div className="card bg-white border border-slate-200 shadow-sm p-6 transition-all duration-200 hover:shadow-md">
             <h3 className="font-semibold text-blue-700 mb-3 flex items-center">
               <span className="bg-blue-50 p-2 rounded-full mr-2">📊</span>
               Progression
             </h3>
             <div className="mb-1">
               <div className="flex justify-between text-sm mb-1">
                  <span className="font-semibold">{progressPercent.toFixed(0)}% complété</span>
                 <span className="text-slate-500 text-xs">
                   {Object.keys(completedSections).filter(id => completedSections[id]).length} / {sections.length} sections
                 </span>
               </div>
               <div className="w-full bg-slate-100 rounded-full h-3 overflow-hidden">
                 <div
                   className="bg-gradient-to-r from-indigo-400 to-purple-500 h-3 rounded-full transition-all duration-500 ease-out"
                   style={{ width: `${progressPercent}%` }}
                 ></div>
               </div>
             </div>
           </div>
        </div>

         {renderLegalAidInfo()}

         <div className="relative mb-8">
           <div className="space-y-4">
             {sections.map(renderSection)}
           </div>
         </div>

         <div className="bg-slate-100 border border-slate-200 rounded-lg p-4 text-xs text-slate-500 mt-8">
            <p className="italic text-center">
              <strong>Avis important :</strong> Estimation générale à titre informatif. Les coûts réels varient. Consultez un avocat.
            </p>
         </div>

       </div>
    </div>
   );
}

export const Component = requireAuth(SeparationCalculator);

export default SeparationCalculator;
